import type { TableProps } from 'antd';

import { DataTable } from '../../../../components/shared';
import { Badge, Dialog } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { soVN } from '../../../../lib/dinh-dang';
import type { RunProfileResult } from '../../types';

type DongDo = NonNullable<RunProfileResult['bangs']>[number];

/**
 * Kết quả "Đo thống kê cột" (port `KetQuaDoThongKe.tsx` native). Lượt đo chạy hàng phút trên CSDL khách; một toast "Đã đo
 * 35 bảng" không nói được nó vừa đo gì, đo trên mẫu hay đủ, bảng nào hỏng và vì sao — đúng những thứ cần để quyết có tin
 * số đo không.
 */
export function KetQuaDoThongKe({ kq, onClose }: { kq: RunProfileResult | null; onClose: () => void }) {
  const { t, locale } = useT();
  if (!kq) return null;
  const soCotBo = (kq.bangs ?? []).reduce((a, b) => a + b.cotKhongDo.length, 0);

  const cot: TableProps<DongDo>['columns'] = [
    {
      title: t('troLyAiKhoKetQuaDo.colBang'),
      dataIndex: 'bang',
      render: (v: string) => <span className="font-medium text-content-primary">{v}</span>,
    },
    {
      title: t('troLyAiKhoKetQuaDo.colDong'),
      dataIndex: 'soDong',
      align: 'end',
      width: 110,
      render: (v: number) => <span className="tabular-nums">{soVN(v, locale)}</span>,
    },
    {
      title: t('troLyAiKhoKetQuaDo.colCot'),
      dataIndex: 'soCot',
      align: 'end',
      width: 100,
      render: (v: number) => <span className="tabular-nums">{soVN(v, locale)}</span>,
    },
    {
      title: t('troLyAiKhoKetQuaDo.colGhiChu'),
      key: 'ghiChu',
      render: (_, b) => (
        <span className="flex flex-wrap items-center gap-1">
          {b.trenMau ? <Badge variant="neutral">{t('troLyAiKhoKetQuaDo.trenMau')}</Badge> : null}
          {b.cotKhongDo.length ? (
            <span className="text-xs text-warning-700" title={b.cotKhongDo.join(', ')}>
              {t('troLyAiKhoKetQuaDo.cotKhongDo', {
                ds: b.cotKhongDo.slice(0, 4).join(', '),
                n: soVN(b.cotKhongDo.length, locale),
              })}
            </span>
          ) : null}
        </span>
      ),
    },
  ];

  const o: [string, string][] = [
    [t('troLyAiKhoKetQuaDo.bang'), soVN(kq.tables, locale)],
    [t('troLyAiKhoKetQuaDo.cot'), soVN(kq.columns, locale)],
    [t('troLyAiKhoKetQuaDo.dong'), soVN(kq.rows, locale)],
    [t('troLyAiKhoKetQuaDo.thoiGian'), t('troLyAiKhoKetQuaDo.giay', { so: soVN(Math.round((kq.ms ?? 0) / 100) / 10, locale) })],
  ];

  return (
    <Dialog open onClose={onClose} width={760} title={t('troLyAiKhoKetQuaDo.tieuDe')}>
      <div className="space-y-4 text-sm">
        <p className="text-xs leading-relaxed text-content-secondary">{t('troLyAiKhoKetQuaDo.moTa')}</p>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {o.map(([nhan, so]) => (
            <div key={nhan} className="rounded-lg bg-subtle px-3 py-2">
              <p className="text-xs text-content-muted">{nhan}</p>
              <p className="font-semibold tabular-nums text-content-primary">{so}</p>
            </div>
          ))}
        </div>

        {kq.boQuaTat ? (
          <p className="text-xs text-content-secondary">
            {t('troLyAiKhoKetQuaDo.boQuaTat', { n: soVN(kq.boQuaTat, locale) })}
          </p>
        ) : null}

        {kq.bangLoi?.length ? (
          <div className="rounded-lg border border-danger-100 bg-danger-50 p-3" role="alert">
            <p className="mb-1.5 text-xs font-semibold text-danger-700">
              {t('troLyAiKhoKetQuaDo.bangLoi', { n: soVN(kq.bangLoi.length, locale) })}
            </p>
            <ul className="space-y-1">
              {kq.bangLoi.map((b) => (
                <li key={b.bang} className="break-words text-xs text-danger-700">
                  <code className="font-medium">{b.bang}</code> — {b.loi}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {soCotBo ? (
          <p className="rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-700">
            {t('troLyAiKhoKetQuaDo.cotBo', { n: soVN(soCotBo, locale) })}
          </p>
        ) : null}

        <DataTable<DongDo>
          caption={t('troLyAiKhoKetQuaDo.tieuDe')}
          rowKey="bang"
          size="small"
          columns={cot}
          dataSource={kq.bangs ?? []}
          pagination={false}
        />
      </div>
    </Dialog>
  );
}
